import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import {
	CATEGORY_COLORS,
	gradeColor,
	ordinal,
	playoffResult,
} from "../util/reportCardHelpers.ts";
import type { View } from "../../common/types.ts";

// One category's diverging bar. Zero sits at the fraction of the [min,max] range
// where 0 falls, so negative categories (Expectations) draw left/red.
const CategoryBar = ({
	label,
	points,
	min,
	max,
	color,
}: {
	label: string;
	points: number;
	min: number;
	max: number;
	color: string;
}) => {
	const span = max - min;
	const zeroFrac = span > 0 ? (0 - min) / span : 0;
	const pointFrac = span > 0 ? (points - min) / span : 0;
	const negative = points < 0;
	const left = Math.min(zeroFrac, pointFrac) * 100;
	const width = Math.abs(pointFrac - zeroFrac) * 100;

	return (
		<div className="d-flex align-items-center mb-2">
			<div style={{ width: 120 }} className="text-body-secondary small">
				{label}
			</div>
			<div
				className="position-relative flex-grow-1 rounded"
				style={{ height: 16, backgroundColor: "var(--bs-secondary-bg)" }}
			>
				{min < 0 ? (
					<div
						className="position-absolute"
						style={{
							left: `${zeroFrac * 100}%`,
							top: -2,
							bottom: -2,
							width: 1,
							backgroundColor: "var(--bs-border-color)",
						}}
					/>
				) : null}
				<div
					className="position-absolute rounded"
					style={{
						left: `${left}%`,
						width: `${Math.max(width, 0.5)}%`,
						top: 0,
						bottom: 0,
						backgroundColor: negative ? "#dc3545" : color,
					}}
				/>
			</div>
			<div
				style={{ width: 64 }}
				className="text-end small fw-medium ms-2 font-monospace"
			>
				{points > 0 ? "+" : ""}
				{points}
			</div>
		</div>
	);
};

type ShareCard = NonNullable<View<"reportCard">["card"]>;

// A self-contained, fixed-size card designed to be rendered to a PNG (html2canvas)
// and shared. Uses only inline hex colors so it looks identical in any theme.
const ShareableCard = ({
	card,
	lost,
	numPlayoffRounds,
	numRanked,
	playoffRoundsWon,
	projectedWins,
	rank,
	season,
	teamName,
	won,
}: {
	card: ShareCard;
	lost: number;
	numPlayoffRounds: number;
	numRanked: number;
	playoffRoundsWon: number;
	projectedWins: number | undefined;
	rank: number | undefined;
	season: number;
	teamName: string;
	won: number;
}) => {
	const color = gradeColor(card.grade);
	const diff =
		projectedWins !== undefined
			? Math.round((won - projectedWins) * 10) / 10
			: undefined;

	return (
		<div
			style={{
				width: 540,
				fontFamily: "Helvetica, Arial, sans-serif",
				background: "#ffffff",
				color: "#1a1a1a",
				borderRadius: 20,
				overflow: "hidden",
				boxSizing: "border-box",
			}}
		>
			{/* Banner */}
			<div
				style={{
					background: `linear-gradient(135deg, ${color}, ${color}cc)`,
					color: "#ffffff",
					padding: "28px 32px 56px",
				}}
			>
				<div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
					{teamName}
					{card.champion ? " 🏆" : ""}
				</div>
				<div style={{ fontSize: 17, opacity: 0.85, marginTop: 2 }}>
					{season} Season Report Card
				</div>
			</div>

			{/* Grade + score */}
			<div style={{ display: "flex", alignItems: "center", padding: "0 32px" }}>
				<div
					style={{
						width: 132,
						height: 132,
						borderRadius: "50%",
						background: "#ffffff",
						boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
						marginTop: -46,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<span style={{ fontSize: 56, fontWeight: 700, color }}>
						{card.grade}
					</span>
				</div>
				<div style={{ marginLeft: 24, paddingTop: 12 }}>
					<div style={{ fontSize: 52, fontWeight: 700, lineHeight: 1 }}>
						{card.gmScore}
					</div>
					<div style={{ fontSize: 15, color: "#6b7280", marginTop: 2 }}>
						GM SCORE
						{rank !== undefined
							? ` · ${ordinal(rank)} of ${numRanked}`
							: ""}
					</div>
				</div>
			</div>

			{/* Summary */}
			<div
				style={{
					display: "flex",
					gap: 28,
					padding: "20px 32px 4px",
					fontSize: 15,
				}}
			>
				<div>
					<div style={{ color: "#6b7280", fontSize: 12 }}>RECORD</div>
					<div style={{ fontWeight: 600 }}>
						{won}–{lost}
					</div>
				</div>
				<div>
					<div style={{ color: "#6b7280", fontSize: 12 }}>OVER/UNDER</div>
					<div style={{ fontWeight: 600 }}>
						{projectedWins !== undefined ? (
							<>
								{projectedWins}
								{diff !== undefined && diff !== 0 ? (
									<span
										style={{
											color: diff > 0 ? "#198754" : "#dc3545",
											marginLeft: 4,
										}}
									>
										({diff > 0 ? "+" : ""}
										{diff})
									</span>
								) : null}
							</>
						) : (
							"—"
						)}
					</div>
				</div>
				<div>
					<div style={{ color: "#6b7280", fontSize: 12 }}>PLAYOFFS</div>
					<div style={{ fontWeight: 600 }}>
						{playoffResult(playoffRoundsWon, numPlayoffRounds)}
					</div>
				</div>
			</div>

			{/* Category bars */}
			<div style={{ padding: "12px 32px 8px" }}>
				{card.categories.map((c) => {
					const span = c.max - c.min;
					const frac = span > 0 ? Math.max(0, c.points) / c.max : 0;
					return (
						<div
							key={c.key}
							style={{ display: "flex", alignItems: "center", marginBottom: 7 }}
						>
							<div style={{ width: 128, fontSize: 13, color: "#4b5563" }}>
								{c.label}
							</div>
							<div
								style={{
									flexGrow: 1,
									height: 12,
									background: "#e9ecef",
									borderRadius: 6,
									overflow: "hidden",
								}}
							>
								<div
									style={{
										width: `${Math.min(frac, 1) * 100}%`,
										height: "100%",
										background:
											c.points < 0
												? "#dc3545"
												: (CATEGORY_COLORS[c.key] ?? "#0d6efd"),
									}}
								/>
							</div>
							<div
								style={{
									width: 44,
									textAlign: "right",
									fontSize: 13,
									fontWeight: 600,
									marginLeft: 8,
								}}
							>
								{c.points > 0 ? "+" : ""}
								{c.points}
							</div>
						</div>
					);
				})}
			</div>

			{/* Footer */}
			<div
				style={{
					borderTop: "1px solid #e9ecef",
					padding: "14px 32px",
					fontSize: 13,
					color: "#9ca3af",
					letterSpacing: 1,
					textTransform: "uppercase",
					fontWeight: 600,
				}}
			>
				Icons · Season Report Card
			</div>
		</div>
	);
};

const ReportCard = ({
	abbrev,
	card,
	leaderboard,
	lost,
	numPlayoffRounds,
	numRanked,
	playoffRoundsWon,
	projectedWins,
	rank,
	season,
	teamName,
	tid,
	userTid,
	won,
}: View<"reportCard">) => {
	useTitleBar({
		title: "Report Card",
		jumpTo: true,
		jumpToSeason: season,
		dropdownView: "report_card",
		dropdownFields: { teamsAndYours: abbrev, seasons: season },
	});

	const shareRef = useRef<HTMLDivElement>(null);
	const [sharing, setSharing] = useState(false);

	const onShare = async () => {
		const el = shareRef.current;
		if (!el || sharing) {
			return;
		}
		setSharing(true);
		try {
			const canvas = await html2canvas(el, {
				backgroundColor: null,
				scale: 2,
				logging: false,
			});
			const fileName = `${teamName}_${season}_report_card`
				.replace(/[^a-z0-9]+/gi, "_")
				.toLowerCase();
			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve, "image/png"),
			);
			if (!blob) {
				throw new Error("Failed to render image");
			}
			const file = new File([blob], `${fileName}.png`, { type: "image/png" });

			// Prefer the native share sheet (mobile) when it supports files.
			const nav = navigator as Navigator & {
				canShare?: (data?: any) => boolean;
				share?: (data?: any) => Promise<void>;
			};
			if (nav.canShare?.({ files: [file] }) && nav.share) {
				try {
					await nav.share({
						files: [file],
						title: `${teamName} ${season} Report Card`,
					});
					return;
				} catch (error) {
					// User cancelled the share sheet — don't fall through to a download.
					if ((error as { name?: string }).name === "AbortError") {
						return;
					}
				}
			}

			// Fallback: download the PNG.
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${fileName}.png`;
			document.body.append(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1500);
		} catch (error) {
			console.error(error);
			logEvent({
				type: "error",
				text: "Couldn't generate the shareable card.",
				saveToDb: false,
			});
		} finally {
			setSharing(false);
		}
	};

	if (!card) {
		return (
			<div className="alert alert-info" style={{ maxWidth: 640 }}>
				<b>No report card yet.</b> A team's season report card is generated at
				the end of the season, when the draft begins. Play through to the draft
				to see {teamName}'s {season} grade.
			</div>
		);
	}

	const color = gradeColor(card.grade);
	const diff =
		projectedWins !== undefined
			? Math.round((won - projectedWins) * 10) / 10
			: undefined;

	return (
		<>
			{/* Hero */}
			<div
				className="d-flex align-items-center gap-4 p-3 mb-3 rounded text-white"
				style={{
					background: `linear-gradient(135deg, ${color}, ${color}bb)`,
					maxWidth: 720,
				}}
			>
				<div
					className="d-flex flex-column align-items-center justify-content-center rounded-circle bg-white flex-shrink-0"
					style={{ width: 104, height: 104 }}
				>
					<div
						className="fw-bold"
						style={{ fontSize: 44, lineHeight: 1, color }}
					>
						{card.grade}
					</div>
				</div>
				<div className="flex-grow-1">
					<div className="h4 mb-0">
						{teamName}
						{card.champion ? " 🏆" : ""}
					</div>
					<div className="opacity-75">{season} season</div>
					<div className="mt-2 d-flex align-items-baseline gap-2">
						<span className="display-6 fw-bold font-monospace">
							{card.gmScore}
						</span>
						<span className="opacity-75">GM Score</span>
					</div>
					{rank !== undefined ? (
						<div className="mt-1">
							Ranked <b>{ordinal(rank)}</b> of {numRanked} this season
						</div>
					) : null}
				</div>
			</div>

			<div className="mb-3">
				<button
					type="button"
					className="btn btn-primary"
					onClick={onShare}
					disabled={sharing}
					title="Save or share this report card as an image"
				>
					{sharing ? "Rendering…" : "📸 Share Card"}
				</button>
			</div>

			<div className="row" style={{ maxWidth: 720 }}>
				{/* Summary */}
				<div className="col-12 mb-3">
					<div className="d-flex flex-wrap gap-4">
						<div>
							<div className="text-body-secondary small">Record</div>
							<div className="fw-medium">
								{won}–{lost}
							</div>
						</div>
						<div>
							<div className="text-body-secondary small">Over/Under</div>
							<div className="fw-medium">
								{projectedWins !== undefined ? (
									<>
										{projectedWins} line
										{diff !== undefined && diff !== 0 ? (
											<span
												className={
													diff > 0 ? "text-success ms-1" : "text-danger ms-1"
												}
											>
												({diff > 0 ? "+" : ""}
												{diff})
											</span>
										) : null}
									</>
								) : (
									"—"
								)}
							</div>
						</div>
						<div>
							<div className="text-body-secondary small">Playoffs</div>
							<div className="fw-medium">
								{playoffResult(playoffRoundsWon, numPlayoffRounds)}
							</div>
						</div>
					</div>
				</div>

				{/* Category breakdown */}
				<div className="col-12 mb-3">
					<h3 className="h6 text-body-secondary text-uppercase mb-3">
						How the grade breaks down
					</h3>
					{card.categories.map((c) => (
						<CategoryBar
							key={c.key}
							label={c.label}
							points={c.points}
							min={c.min}
							max={c.max}
							color={CATEGORY_COLORS[c.key] ?? "#0d6efd"}
						/>
					))}
				</div>

				{/* Mini leaderboard */}
				<div className="col-12">
					<h3 className="h6 text-body-secondary text-uppercase mb-2">
						League leaderboard
					</h3>
					<table className="table table-sm table-hover align-middle">
						<tbody>
							{leaderboard.map((row, i) => {
								const isThis = row.tid === tid;
								const isUser = row.tid === userTid;
								return (
									<tr
										key={row.tid}
										className={isThis ? "table-active fw-bold" : undefined}
									>
										<td style={{ width: 32 }} className="text-body-secondary">
											{i + 1}
										</td>
										<td>
											<a
												href={helpers.leagueUrl([
													"report_card",
													`${row.abbrev}_${row.tid}`,
													season,
												])}
											>
												{row.region} {row.name}
											</a>
											{row.champion ? " 🏆" : ""}
											{isUser ? (
												<span className="badge text-bg-secondary ms-2">You</span>
											) : null}
										</td>
										<td style={{ width: 48 }} className="text-end fw-bold">
											<span style={{ color: gradeColor(row.grade) }}>
												{row.grade}
											</span>
										</td>
										<td
											style={{ width: 64 }}
											className="text-end font-monospace text-body-secondary"
										>
											{row.gmScore}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			{/* Off-screen source for the shareable PNG (rendered but not visible). */}
			<div
				aria-hidden
				style={{ position: "fixed", left: -10000, top: 0, zIndex: -1 }}
			>
				<div ref={shareRef}>
					<ShareableCard
						card={card}
						lost={lost}
						numPlayoffRounds={numPlayoffRounds}
						numRanked={numRanked}
						playoffRoundsWon={playoffRoundsWon}
						projectedWins={projectedWins}
						rank={rank}
						season={season}
						teamName={teamName}
						won={won}
					/>
				</div>
			</div>
		</>
	);
};

export default ReportCard;
